import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent, CartAddPendingEvent, CartQuantityRefreshedEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

// CARLYLE: how long the drawer is allowed to sit in the optimistic-open pending/skeleton state
// before force-exiting it, in case every normal exit path (CartAddEvent, CartErrorEvent) somehow
// gets missed (offline, a thrown exception in another listener, etc). Belt-and-suspenders for the
// error-path fix below — pending state must never be able to hang indefinitely.
const PENDING_STATE_TIMEOUT_MS = 8000;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  // CARLYLE — optimistic open (perf fix, 2026-07-26): tracks the DOM inserted/locked by
  // #enterPendingState so #exitPendingState can precisely undo it, even though in practice the
  // real cart-items morph (component-cart-items.js, driven by the same CartAddEvent) usually
  // clears the pending row itself. Explicit cleanup here is a defensive belt-and-suspenders pass,
  // not a dependency — see #exitPendingState.
  /** @type {{ remove: () => void } | null} */
  #pendingRow = null;
  /** @type {Array<{ el: HTMLElement, focusables: Array<{ node: Element, prevTabIndex: string | null }> }>} */
  #pendingRailEls = [];
  /** @type {number | null} */
  #pendingTimeoutId = null;
  // CARLYLE — E: set synchronously the instant CartAddPendingEvent is handled, BEFORE the rAF that
  // actually enters pending state is queued. If a real CartAddEvent/CartErrorEvent lands (and calls
  // #exitPendingState, which clears this) before that rAF fires — a near-zero-latency/cached/mocked
  // response — the queued #enterPendingState bails instead of entering pending state AFTER we've
  // already exited it, which would otherwise orphan a skeleton row forever.
  #pendingRequested = false;
  // CARLYLE — F: the dialog's aria-labelledby swapped away from the (stale/misleading mid-add)
  // empty-cart heading; restored verbatim in #exitPendingState.
  /** @type {string | null} */
  #pendingPrevLabelledBy = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartAddPendingEvent.eventName, this.#handleCartAddPending);
    document.addEventListener(CartQuantityRefreshedEvent.eventName, this.#handleQuantityRefreshed);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    // `toggle` does NOT bubble, so this must be a CAPTURING listener — capture still reaches
    // ancestors on the way down to the target. Delegated rather than bound to the <details> itself
    // because cart morphs replace that node on every cart change.
    this.addEventListener('toggle', this.#handleExpressToggle, true);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartAddPendingEvent.eventName, this.#handleCartAddPending);
    document.removeEventListener(CartQuantityRefreshedEvent.eventName, this.#handleQuantityRefreshed);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('toggle', this.#handleExpressToggle, true);
    this.#historyAbortController?.abort();
  }

  /**
   * Reveal the accelerated-wallet stack when "More payment options" is expanded.
   *
   * The disclosure sits at the bottom of the scrolling summary, so opening it renders the wallets
   * below the fold — the shopper taps, sees the caret flip, and has to scroll to find what they
   * just asked for. This scrolls them into view instead.
   *
   * Scrolls the drawer's own scrollport explicitly rather than calling scrollIntoView(), which
   * walks up and can scroll the page behind the dialog as well.
   *
   * @param {Event} event
   */
  #handleExpressToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('ca-express')) return;
    if (!details.open) return;

    const scroller = this.querySelector('.cart-drawer__content');
    if (!(scroller instanceof HTMLElement)) return;

    // Wait a frame so the revealed content has been laid out and the scrollHeight is real.
    requestAnimationFrame(() => {
      const overshoot = details.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom;
      if (overshoot <= 0) return;

      // Clear ScrollHint's fade as well as the fold. Landing the disclosure flush with the bottom
      // edge is not enough: the summary's own bottom padding means there is still a little scroll
      // left, so the scroller keeps painting its bottom mask and the last wallet button renders
      // under a gradient. Overshooting by the fade band (10% of the scrollport, see #update in
      // assets/scrolling.js) pushes past it — and clamps harmlessly at max scroll, where the mask
      // goes fully opaque anyway.
      const fadeBand = scroller.clientHeight * 0.1;

      scroller.scrollTo({
        top: scroller.scrollTop + overshoot + fadeBand,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // CARLYLE: whatever pending/skeleton state #enterPendingState set up is stale the instant a
    // real CartAddEvent/CartUpdateEvent lands (the real morph is about to run, or already has) —
    // always clear it here, even for tagged sources below. Cheap no-op if nothing is pending.
    this.#exitPendingState();

    if (event.detail?.data?.didError) return;

    // A successful cart change supersedes any previous failure — don't leave a stale error banner
    // sitting above a cart that has since updated fine.
    this.#showError(null);

    // `CartAddEvent` and `CartUpdateEvent` share the DOM event name `cart:update`, so this
    // add-handler also receives plain updates. Tagged sources own their feedback and must not
    // auto-open the drawer.
    const taggedSource = event.detail?.data?.source;
    if (taggedSource === 'cart-upsell' || taggedSource === 'gift-wrapping' || taggedSource === 'saved-for-later' || taggedSource === 'ca-quick-add') {
      // See #revealCartAfterRailAdd.
      if (taggedSource === 'cart-upsell') {
        this.#revealCartAfterRailAdd();
      }
      // A rail quick-add removes the tile holding focus; saving a line removes its row the same
      // way. Both drop focus to <body> inside an open modal dialog.
      requestAnimationFrame(() => this.#restoreFocusIfLost());
      return;
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    // CARLYLE — H: the cart-items morph this same event also triggers (component-cart-items.js,
    // a separate listener on this identical event) can replace the subtree that currently holds
    // focus — the empty-cart "Continue shopping" button, or our own pending row — dropping focus to
    // <body>. Check one frame later (after that morph has had a chance to run) and recover it onto
    // a stable, always-present anchor inside the drawer. Kept in this Carlyle-owned component
    // rather than the base morph file per the a11y review.
    requestAnimationFrame(() => this.#restoreFocusIfLost());
  };

  /**
   * Show the shopper what a rail quick-add actually did.
   *
   * A quick-add from an upsell rail inserts the new line at the TOP of the item list, but the
   * shopper is scrolled down at the rail — so from where they are sitting, the only thing that
   * happens is the tile collapsing away. The cart count, the free-shipping progress bar and the new
   * line item all change off-screen. Scrolling back to the top puts all three in view at once, and
   * for the free-shipping rail specifically that progress bar advancing IS the payoff for the add.
   *
   * Runs after a frame so the cart morph (a separate listener on this same event) has replaced the
   * item list first — scrolling before that lands would target the pre-morph layout.
   *
   * Deliberately does NOT announce here: cart-upsell.js already announces the add through its own
   * `data-upsell-live` region, and the event it dispatches carries `resource: null` by design (its
   * consumers self-fetch totals), so there is no item_count to announce even if we wanted to.
   */
  #revealCartAfterRailAdd() {
    // Nothing to reveal if the drawer isn't open — on the /cart page the rails live in the page,
    // not in this dialog.
    if (!this.refs.dialog?.open) return;

    requestAnimationFrame(() => {
      const scroller = this.querySelector('.cart-drawer__content');
      if (!(scroller instanceof HTMLElement) || scroller.scrollTop === 0) return;

      scroller.scrollTo({
        top: 0,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });
  }

  /**
   * CARLYLE — D: cleanup on a failed/offline /cart/add.js. Without this the drawer was measured
   * getting stuck open in the pending skeleton (aria-busy="true") forever on any network failure,
   * since CartErrorEvent previously had no listener here at all.
   */
  #handleCartError = (event) => {
    this.#exitPendingState();

    // CARLYLE (2026-08-01): actually TELL the shopper. Clearing the skeleton and leaving the drawer
    // open on the unchanged cart made a failure look identical to a rendering glitch. The message is
    // already on the event (see CartErrorEvent in assets/events.js); the PDP's own error slot is
    // unusable here because it sits under the modal in the top layer.
    const data = /** @type {CustomEvent} */ (event)?.detail?.data;
    const description = typeof data?.description === 'string' ? data.description.trim() : '';
    this.#showError(description || data?.message);
  };

  /**
   * Show (or clear) the drawer's failed-add banner.
   * @param {string | undefined | null} message - falsy clears and hides the banner.
   */
  #showError(message) {
    const banner = /** @type {HTMLElement | undefined} */ (this.refs.cartError);
    const text = /** @type {HTMLElement | undefined} */ (this.refs.cartErrorText);
    if (!banner || !text) return;

    if (!message) {
      banner.hidden = true;
      text.textContent = '';
      return;
    }

    text.textContent = message;
    banner.hidden = false;
  }

  /**
   * CARLYLE — optimistic open (perf fix, 2026-07-26): opens the drawer the INSTANT add-to-cart is
   * clicked, before /cart/add.js has even been sent, instead of waiting ~1.5-2s for the server
   * render + (formerly) a redundant follow-up fetch. Gated on the same `auto-open` attribute the
   * real CartAddEvent handler already respects, so a merchant who disabled auto-open sees no
   * behavior change at all. See #enterPendingState for the skeleton/reserved-height treatment.
   * @param {CustomEvent<{ sourceId: string }>} _event
   */
  #handleCartAddPending = (_event) => {
    this.#showError(null);

    if (!this.hasAttribute('auto-open')) return;

    this.showDialog(); // no-op if already open (see @theme/dialog#showDialog's `if (dialog.open) return;` guard)

    // CARLYLE — E: mark a pending request as in-flight BEFORE queuing the rAF below (synchronous),
    // so a same-tick #exitPendingState (see #handleCartAdd/#handleCartError) can cancel it.
    this.#pendingRequested = true;

    // #enterPendingState measures rail heights via getBoundingClientRect(), which reads 0 for
    // anything inside a <dialog> that hasn't been shown yet — showDialog() defers its actual
    // `dialog.showModal()` call into a requestAnimationFrame (see @theme/dialog). Queuing our own
    // rAF here runs it right after that one in the same frame (rAF callbacks fire in registration
    // order), so the dialog is guaranteed open and laid out before we measure anything.
    requestAnimationFrame(() => {
      if (!this.#pendingRequested) return; // exited (real response, or error) before we got here
      this.#enterPendingState();
    });
  };

  /**
   * CARLYLE — perf fix (2026-07-26): the /cart.js quantity refetch that used to block CartAddEvent
   * now runs fire-and-forget (see product-form.js). This is its result arriving a beat later —
   * only used for the accessible live-region count, which doesn't need to be synchronous with the
   * drawer opening.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleQuantityRefreshed = (event) => {
    this.#announceCartCount(event.detail?.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * CARLYLE — optimistic open (perf fix, 2026-07-26): the visual treatment shown between the
   * instant open and the real cart-items morph landing. Two different treatments by design (per
   * the CRO spec this shipped against):
   *  - The shopper's own new line item gets a REAL pending row (reusing the `.ca-cart-pending`
   *    scaffold cart-upsell.js already built for in-drawer quick-adds) — content-shaped feedback,
   *    swapped for the real row the instant the morph runs. Never a bare shimmer.
   *  - The two upsell rails (`cart-upsell` / `cart-freeship-upsell`) — which, in this pass, are
   *    STILL server-rendered as part of the same /cart/add.js response (true lazy client-side
   *    fetch of the rails was assessed too high-blast-radius to do safely in this pass — see the
   *    perf audit; flagged as a shopify-architect follow-up) — get a dimmed shimmer overlay instead
   *    of new content, because there's no separate "rails still loading" phase to represent: they
   *    resolve at the exact same moment as the line item. The shimmer here is purely honest "cart
   *    is updating" feedback, not a real loading state per rail.
   *
   * CLS: rail height is LOCKED via measured `min-height` (each rail's own current rendered height,
   * the only accurate proxy we have for "true final height" without seeing the new data) rather
   * than a hardcoded guess, and ONLY for rails that are currently visible (have cards) — a rail
   * that's currently hidden is left alone rather than guessing it will appear, since a wrong guess
   * (reserving space that collapses back to 0) would itself cause a shift.
   */
  #enterPendingState() {
    // Guard against a rapid double-click / double add-to-cart re-entering pending state on top of
    // itself (would insert a second pending row and re-measure already-locked rail heights).
    if (this.#pendingRow) return;

    const dialog = this.refs.dialog;

    // aria-busy tells assistive tech the drawer's content is still settling — meaningful signal,
    // not just a styling hook. Cleared in #exitPendingState the instant the real morph lands.
    if (dialog) dialog.setAttribute('aria-busy', 'true');

    // CARLYLE — F: on a first add from an empty cart, the dialog's accessible name is currently
    // "Your cart is empty" (aria-labelledby → the empty-state heading) — stale and actively wrong
    // the moment we're showing a pending item. Swap to the header cart-trigger button's own
    // (already-translated) label rather than hardcoding new copy; restored verbatim on exit.
    if (dialog && dialog.getAttribute('aria-labelledby') === 'cart-drawer-heading-empty') {
      this.#pendingPrevLabelledBy = dialog.getAttribute('aria-labelledby');
      const neutralLabel = document
        .querySelector('[data-testid="cart-drawer-trigger"]')
        ?.getAttribute('aria-label');
      dialog.removeAttribute('aria-labelledby');
      dialog.setAttribute('aria-label', neutralLabel || 'Cart');
    }

    // CARLYLE — B: belt-and-suspenders — force-exit if no real CartAddEvent/CartErrorEvent lands
    // within a reasonable window (offline, a thrown exception elsewhere, anything unforeseen).
    // Pending state must never be able to hang indefinitely.
    this.#pendingTimeoutId = window.setTimeout(() => this.#exitPendingState(), PENDING_STATE_TIMEOUT_MS);

    this.#pendingRow = this.#insertPendingLineItem();
    this.#pendingRailEls = this.#lockRailHeights();
  }

  /**
   * Reverts everything #enterPendingState did. Safe to call when nothing is pending (no-op) and
   * safe to call after the real morph already removed/repurposed the pending row itself.
   */
  #exitPendingState() {
    // CARLYLE — E: cancels any #enterPendingState rAF still in flight (see #handleCartAddPending).
    this.#pendingRequested = false;

    if (this.#pendingTimeoutId !== null) {
      window.clearTimeout(this.#pendingTimeoutId);
      this.#pendingTimeoutId = null;
    }

    const dialog = this.refs.dialog;
    if (dialog) {
      dialog.removeAttribute('aria-busy');
      if (this.#pendingPrevLabelledBy) {
        dialog.setAttribute('aria-labelledby', this.#pendingPrevLabelledBy);
        dialog.removeAttribute('aria-label');
        this.#pendingPrevLabelledBy = null;
      }
    }

    this.#pendingRow?.remove();
    this.#pendingRow = null;

    for (const { el, focusables } of this.#pendingRailEls) {
      if (el.isConnected) {
        el.style.removeProperty('min-height');
        el.classList.remove('ca-rail-pending');
      }
      for (const { node, prevTabIndex } of focusables) {
        if (!node.isConnected) continue;
        if (prevTabIndex === null) node.removeAttribute('tabindex');
        else node.setAttribute('tabindex', prevTabIndex);
      }
    }
    this.#pendingRailEls = [];
  }

  /**
   * CARLYLE — H: restores focus to a stable anchor if the element that had it got removed by the
   * real cart-items morph (e.g. the empty-cart "Continue shopping" button, or our own pending row —
   * though that's removed via the guarded #insertPendingLineItem closures, not by the morph itself).
   * No-op if focus is still somewhere sensible.
   */
  #restoreFocusIfLost() {
    const dialog = this.refs.dialog;
    if (!dialog?.open) return;

    const active = document.activeElement;
    if (active && active !== document.body && dialog.contains(active)) return;

    const closeButton = /** @type {HTMLElement | null} */ (dialog.querySelector('.cart-drawer__close-button'));
    closeButton?.focus();
  }

  /**
   * Inserts a skeleton line-item row (thumbnail-less: the pending event doesn't carry product
   * media, kept deliberately minimal/low-risk — see perf audit) at the top of the cart items list,
   * matching where the real just-added row will land. Mirrors the tbody-row vs. empty-cart-standalone
   * branching in cart-upsell.js's insertPendingRow (same `.ca-cart-pending` / `.ca-cart-pending--standalone`
   * scaffold, intentionally reused rather than duplicated with new CSS).
   * @returns {{ remove: () => void } | null}
   */
  #insertPendingLineItem() {
    try {
      const fillInner = (el) => {
        el.classList.add('ca-cart-pending__inner');
        const thumb = document.createElement('span');
        thumb.className = 'ca-cart-pending__thumb';
        const lines = document.createElement('span');
        lines.className = 'ca-cart-pending__lines';
        const titleBar = document.createElement('span');
        titleBar.className = 'ca-cart-pending__bar ca-cart-pending__bar--title';
        const priceBar = document.createElement('span');
        priceBar.className = 'ca-cart-pending__bar ca-cart-pending__bar--price';
        lines.append(titleBar, priceBar);
        const spinner = document.createElement('span');
        spinner.className = 'ca-cart-pending__spinner';
        el.append(thumb, lines, spinner);
      };

      const tbody = this.querySelector('.cart-items__table tbody');
      if (tbody) {
        const row = document.createElement('tr');
        row.className = 'ca-cart-pending';
        row.setAttribute('data-ca-pending', '');
        row.setAttribute('aria-hidden', 'true');
        const cell = document.createElement('td');
        fillInner(cell);
        row.appendChild(cell);
        // Most-recently-added line renders first — placeholder goes at the top to match.
        tbody.insertBefore(row, tbody.firstChild);
        // CARLYLE — D: guard the removal. Horizon's cart morph can match/repurpose this exact <tr>
        // IN PLACE (positional morph) rather than replacing it wholesale — if that happened, `row`
        // now points at the REAL just-added line, and an unguarded .remove() would delete it. The
        // morph reconciles attributes to the server markup, which never carries `data-ca-pending`,
        // so its presence reliably means "this is still our skeleton, never touched by a real morph."
        return { remove: () => { if (row.isConnected && row.hasAttribute('data-ca-pending')) row.remove(); } };
      }

      // Empty cart, first add: no table yet — standalone skeleton in the items wrapper, same
      // top-align handling (`ca-first-add-pending`) the existing quick-add scaffold already has
      // CSS for (snippets/cart-upsell.liquid).
      const wrapper = this.querySelector('.cart-items__wrapper');
      if (!wrapper) return null;

      const block = document.createElement('div');
      block.className = 'ca-cart-pending ca-cart-pending--standalone';
      block.setAttribute('data-ca-pending', '');
      block.setAttribute('aria-hidden', 'true');
      const innerDiv = document.createElement('div');
      fillInner(innerDiv);
      block.appendChild(innerDiv);

      const hidden = [];
      for (const el of wrapper.children) {
        if (el instanceof HTMLElement && el !== block) {
          hidden.push([el, el.style.display]);
          el.style.display = 'none';
        }
      }

      const prevWrapperDisplay = wrapper.style.display;
      const prevWrapperAlign = wrapper.style.textAlign;
      wrapper.style.display = 'block';
      wrapper.style.textAlign = 'start';
      wrapper.appendChild(block);

      const dialog = this.refs.dialog;
      if (dialog) dialog.classList.add('ca-first-add-pending');

      return {
        // CARLYLE — D: same guarded-removal reasoning as the tbody row above — only remove `block`
        // if it's still our untouched skeleton. The sibling-visibility/wrapper restores are safe to
        // run unconditionally: if the morph already replaced this whole subtree (empty→non-empty
        // cart swaps the entire template), `hidden`/`wrapper` either no longer matter or setting
        // inline styles on now-detached nodes is a harmless no-op.
        remove: () => {
          if (block.isConnected && block.hasAttribute('data-ca-pending')) block.remove();
          hidden.forEach(([el, display]) => {
            el.style.display = display;
          });
          wrapper.style.display = prevWrapperDisplay;
          wrapper.style.textAlign = prevWrapperAlign;
          if (dialog) dialog.classList.remove('ca-first-add-pending');
        },
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Locks the rendered height of any CURRENTLY VISIBLE upsell rail (has at least one card) and
   * flags it with `.ca-rail-pending` (dimmed content + shimmer overlay, static in
   * prefers-reduced-motion — see snippets/cart-upsell.liquid). Rails that are hidden right now are
   * left untouched — see the CLS reasoning in #enterPendingState's doc comment.
   *
   * CARLYLE — I: the rail's real links/buttons are dimmed, not removed, so they're still live
   * underneath the shimmer overlay and would otherwise stay tab-reachable while visually looking
   * disabled/loading. tabindex="-1" pulls them out of the tab order for the pending window only;
   * original tabindex (usually none at all) is restored per-element in #exitPendingState.
   * @returns {Array<{ el: HTMLElement, focusables: Array<{ node: Element, prevTabIndex: string | null }> }>}
   */
  #lockRailHeights() {
    const locked = [];
    const rails = this.querySelectorAll('.cart-upsell');
    for (const rail of rails) {
      if (!(rail instanceof HTMLElement)) continue;
      if (!rail.querySelector('.cart-upsell__card')) continue; // not currently visible — don't guess
      rail.style.minHeight = `${rail.getBoundingClientRect().height}px`;
      rail.classList.add('ca-rail-pending');

      const focusables = [];
      for (const node of rail.querySelectorAll('a, button')) {
        focusables.push({ node, prevTabIndex: node.getAttribute('tabindex') });
        node.setAttribute('tabindex', '-1');
      }

      locked.push({ el: rail, focusables });
    }
    return locked;
  }

  open() {
    // A shopper deliberately opening the drawer is a fresh look at the cart — never greet them with
    // an error left over from an earlier failed add.
    this.#showError(null);
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
